import {IconButton, InfoTooltip, NumberField} from '@wandb/components';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React from 'react';

import {maybePluralizeWord} from '../../../util/uihelpers';
import {SettingLabel} from '../../WorkspaceDrawer/Settings/components/SettingLabel';
import {type useTrackSettingsChange} from '../../WorkspaceDrawer/Settings/hooks/useTrackSettingEvent';
import {
  DEFAULT_LINE_PLOT_NUM_RUNS_NEW,
  DEFAULT_METRIC_LIMIT,
} from '../../WorkspaceDrawer/Settings/runLinePlots/maxRunDefaults';
import {usePanelConfigContext} from '../PanelConfigContext';
import {type RunsLinePlotConfig} from '../types';
import {useDebouncedCombinedLimit} from './useDebouncedCombinedLimit';

/**
 * Alternative UI for configuring max chart lines via separate metric and run limits.
 *
 * Replaces EditMaxRuns when the `combined_metrics_runs_limit_ui` feature flag is enabled.
 * Users configure: (# metrics) × (# runs) = total lines, subject to maxRunsLimit.
 *
 * The constraint `metricLimit * limit <= maxRunsLimit` is enforced in real-time:
 * when one value changes, the other is automatically adjusted down if needed.
 */
export const CombinedMetricsRunsUI = ({
  config,
  trackPanelSettingsChange,
  updateConfig,
}: {
  config: RunsLinePlotConfig;
  trackPanelSettingsChange: ReturnType<typeof useTrackSettingsChange>;
  updateConfig: (config: RunsLinePlotConfig) => void;
}) => {
  const {limit, maxRunsLimit, metricLimit} = usePanelConfigContext();

  const {
    localMetricLimit,
    localLimit,
    handleMetricLimitChange,
    handleRunLimitChange,
    handleReset,
  } = useDebouncedCombinedLimit({
    config,
    updateConfig,
    maxRunsLimit,
    metricLimit: metricLimit ?? DEFAULT_METRIC_LIMIT,
    limit: limit ?? DEFAULT_LINE_PLOT_NUM_RUNS_NEW,
    onCommit: values => {
      if (values.metricLimit != null) {
        trackPanelSettingsChange({
          setting: 'metricLimit',
          settingValue: values.metricLimit.toString(),
        });
      }
      if (values.limit != null) {
        trackPanelSettingsChange({
          setting: 'limit',
          settingValue: values.limit.toString(),
        });
      }
    },
  });

  return (
    <TailwindContents>
      <fieldset>
        <div className="flex items-center justify-between">
          <div className="flex-grow flex items-center gap-4 justify-start">
            <div className="flex-grow-0">
              <SettingLabel text="Number of lines" />
            </div>
            <div className="flex-grow-0 flex items-center justify-center">
              <InfoTooltip
                aria-label={`Charts can show up to ${maxRunsLimit} lines. Choose whether to allocate them to more metrics or more runs.`}
                id="combined-metrics-runs-limit"
                variant="info">
                Charts can show up to {maxRunsLimit} lines. Choose whether to
                allocate them to more metrics or more runs.
              </InfoTooltip>
            </div>
          </div>
          <div className="flex-grow-0">
            <IconButton
              aria-label="Reset max number of lines"
              isDisabled={config.limit == null && config.metricLimit == null}
              icon="reload-refresh"
              size="sm"
              variant="secondary"
              onClick={handleReset}
            />
          </div>
        </div>

        <div className="flex items-center justify-start">
          <div className="mr-4">
            <NumberField
              compact
              maxValue={maxRunsLimit}
              minValue={1}
              value={localMetricLimit}
              onChange={handleMetricLimitChange}
            />
          </div>
          <div>{maybePluralizeWord(localMetricLimit, 'metric', 's')} *</div>
          <div className="mx-4">
            <NumberField
              compact
              maxValue={maxRunsLimit}
              minValue={1}
              value={localLimit}
              onChange={handleRunLimitChange}
            />
          </div>
          <div>
            {` ${maybePluralizeWord(localLimit, 'run', 's')} = ${localMetricLimit * localLimit} ${maybePluralizeWord(localMetricLimit * localLimit, 'line', 's')} (${maxRunsLimit} max)`}
          </div>
        </div>
      </fieldset>
    </TailwindContents>
  );
};
